import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as QuantityIcon,
  ChangeCircle as StatusIcon,
  Room as LocationIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchMachineActivities } from '../../store/slices/activitiesSlice';
import { MachineActivity, ActivityType, EntityType } from '../../types/activity';

interface MachineActivityListProps {
  machineId: string;
  limit?: number;
}

const getActivityIcon = (activityType: ActivityType) => {
  switch (activityType) {
    case 'created':
      return <AddIcon />;
    case 'updated':
      return <EditIcon />;
    case 'deleted':
      return <DeleteIcon />;
    case 'quantity_changed':
      return <QuantityIcon />;
    case 'status_changed':
      return <StatusIcon />;
    case 'location_changed':
      return <LocationIcon />;
    default:
      return <EditIcon />;
  }
};

const getActivityColor = (activityType: ActivityType) => {
  switch (activityType) {
    case 'created':
      return 'success';
    case 'updated':
      return 'primary';
    case 'deleted':
      return 'error';
    case 'quantity_changed':
      return 'warning';
    case 'status_changed':
      return 'info';
    case 'location_changed':
      return 'secondary';
    default:
      return 'default';
  }
};

const getEntityTypeLabel = (entityType: EntityType) => {
  switch (entityType) {
    case 'spare_part':
      return 'Ersatzteil';
    case 'operating_supply':
      return 'Betriebsmittel';
    case 'tool':
      return 'Werkzeug';
    case 'manual':
      return 'Anleitung';
    case 'machine':
      return 'Maschine';
    default:
      return entityType;
  }
};

const formatActivityDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `vor ${diffInMinutes} Minute${diffInMinutes !== 1 ? 'n' : ''}`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `vor ${hours} Stunde${hours !== 1 ? 'n' : ''}`;
    } else if (diffInHours < 48) {
      return 'gestern';
    } else if (diffInHours < 168) {
      const days = Math.floor(diffInHours / 24);
      return `vor ${days} Tag${days !== 1 ? 'en' : ''}`;
    } else {
      return format(date, 'dd.MM.yyyy, HH:mm', { locale: de });
    }
  } catch (error) {
    return 'Ungültiges Datum';
  }
};

export const MachineActivityList: React.FC<MachineActivityListProps> = ({ machineId, limit = 10 }) => {
  const dispatch = useAppDispatch();
  const { items, isLoading, error } = useAppSelector((state) => state.activities);

  useEffect(() => {
    if (machineId) {
      dispatch(fetchMachineActivities({ machineId, limit }));
    }
  }, [dispatch, machineId, limit]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Keine Aktivitäten vorhanden
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0}>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {items.map((activity, index) => (
          <React.Fragment key={activity.id}>
            <ListItem alignItems="flex-start">
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: `${getActivityColor(activity.activity_type)}.main` }}>
                  {getActivityIcon(activity.activity_type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" component="span">
                      {activity.description}
                    </Typography>
                    <Chip
                      label={getEntityTypeLabel(activity.entity_type)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                }
                secondary={
                  <React.Fragment>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {activity.user?.username || 'System'} • {formatActivityDate(activity.created_at)}
                    </Typography>
                  </React.Fragment>
                }
              />
            </ListItem>
            {index < items.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

export default MachineActivityList;
